import React, { useState } from "react";
import {
  Download, ArrowUpRight, Eye, FileText, MapPin, TrendingUp, ShieldCheck,
  Briefcase, Sparkles, ClipboardList, Wrench, LineChart, User,
} from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { API } from "../lib/api";

const COPY = {
  en: {
    kicker: "Investment Intelligence · Indonesia",
    eyebrow: "Edition 2026 · Volume I",
    heroTitleA: "Your investment guide to ",
    heroTitleLoc: "Lombok",
    heroTitleB: ", crafted for serious capital.",
    heroLede:
      "A professional briefing on how to enter one of Southeast Asia's most promising real estate markets — with clarity on ownership structures, due diligence, and real market data. Available in English and Spanish.",
    ctaDownload: "Download the guide",
    heroMeta: "36 pages · PDF · Free",
    cardTitle: ["Investing", "in Lombok"],
    cardSub:
      "A strategic guide for international investors entering one of Southeast Asia's most promising real estate markets.",
    stats: [
      { value: "2.5M+", label: "Annual visitors projected", icon: TrendingUp },
      { value: "10–17%", label: "Typical rental yields", icon: FileText },
      { value: "1,035 ha", label: "Mandalika Special Economic Zone", icon: MapPin },
      { value: "30–50%", label: "Discount vs. Bali", icon: ShieldCheck },
    ],
    chooseEyebrow: "Choose your language",
    chooseTitle: "Two editions.\nOne investment thesis.",
    guides: [
      {
        lang: "en", code: "EN",
        title: "Investing in Lombok",
        subtitle: "A strategic guide for international investors.",
        meta: "English edition · 2026",
        filename: "Tenjara-Lombok-Investment-Guide-EN.pdf",
      },
      {
        lang: "es", code: "ES",
        title: "Invertir en Lombok",
        subtitle: "Una guía estratégica para inversores internacionales.",
        meta: "Spanish edition · 2026",
        filename: "Tenjara-Lombok-Guia-de-Inversion-ES.pdf",
      },
    ],
    downloadBtn: "Download PDF",
    previewBtn: "Preview in browser",
    preparing: "Preparing…",

    mgmtEyebrow: "Truly passive investment",
    mgmtTitle: "You own the asset.\nWe run the operation.",
    mgmtLede:
      "Investing in Lombok should not mean managing a villa from abroad. Our team takes full operational control of the property — so investors receive monthly income without ever stepping on the island.",
    mgmtPoints: [
      { icon: Sparkles, title: "Marketing & distribution", text: "Airbnb, Booking.com, Expedia, VRBO, direct channels and preferred-partner agencies." },
      { icon: User, title: "Guest experience", text: "Communication, check-in, concierge and 24/7 support — consistent, on-brand hospitality." },
      { icon: Wrench, title: "Maintenance & staff", text: "Preventive upkeep, pool and garden care, staff hiring, training and payroll." },
      { icon: ClipboardList, title: "Accounting & tax", text: "Monthly statements, itemised expenses, local tax filings and compliance." },
      { icon: LineChart, title: "Transparent reporting", text: "Occupancy, revenue vs. forecast, net income and market benchmarks every month." },
      { icon: Briefcase, title: "Single point of contact", text: "One dedicated account manager. One report. No logistics across time zones." },
    ],
    mgmtPull: "Income without operations. Ownership without logistics.",

    insideEyebrow: "Inside the guide",
    insideTitle: "Nine chapters written for decision-makers.",
    chapters: [
      "Introduction — the Lombok opportunity",
      "Why Lombok is a strategic market",
      "Buying off-plan property",
      "Buying a completed villa",
      "Buying land in Lombok",
      "Foreign ownership structures",
      "Why professional guidance matters",
      "Full property management — truly passive ownership",
      "Let's discuss your investment goals",
    ],
  },
  es: {
    kicker: "Inteligencia de Inversión · Indonesia",
    eyebrow: "Edición 2026 · Volumen I",
    heroTitleA: "Tu guía de inversión para ",
    heroTitleLoc: "Lombok",
    heroTitleB: ", pensada para capital serio.",
    heroLede:
      "Un briefing profesional sobre cómo entrar en uno de los mercados inmobiliarios más prometedores del sudeste asiático — con claridad sobre estructuras de propiedad, due diligence y datos reales de mercado. Disponible en inglés y español.",
    ctaDownload: "Descargar la guía",
    heroMeta: "36 páginas · PDF · Gratis",
    cardTitle: ["Invertir", "en Lombok"],
    cardSub:
      "Una guía estratégica para inversores internacionales que desean entrar en uno de los mercados inmobiliarios con mayor proyección del sudeste asiático.",
    stats: [
      { value: "2,5M+", label: "Visitantes anuales proyectados", icon: TrendingUp },
      { value: "10–17%", label: "Rentabilidad típica por alquiler", icon: FileText },
      { value: "1.035 ha", label: "Zona Económica Especial de Mandalika", icon: MapPin },
      { value: "30–50%", label: "Descuento frente a Bali", icon: ShieldCheck },
    ],
    chooseEyebrow: "Elige tu idioma",
    chooseTitle: "Dos ediciones.\nUna tesis de inversión.",
    guides: [
      {
        lang: "en", code: "EN",
        title: "Investing in Lombok",
        subtitle: "A strategic guide for international investors.",
        meta: "Edición en inglés · 2026",
        filename: "Tenjara-Lombok-Investment-Guide-EN.pdf",
      },
      {
        lang: "es", code: "ES",
        title: "Invertir en Lombok",
        subtitle: "Una guía estratégica para inversores internacionales.",
        meta: "Edición en español · 2026",
        filename: "Tenjara-Lombok-Guia-de-Inversion-ES.pdf",
      },
    ],
    downloadBtn: "Descargar PDF",
    previewBtn: "Ver en el navegador",
    preparing: "Preparando…",

    mgmtEyebrow: "Inversión verdaderamente pasiva",
    mgmtTitle: "Tú eres el propietario.\nNosotros operamos.",
    mgmtLede:
      "Invertir en Lombok no debería significar gestionar una villa desde el extranjero. Nuestro equipo asume el control operativo completo de la propiedad — para que el inversor reciba ingresos mensuales sin tener siquiera que pisar la isla.",
    mgmtPoints: [
      { icon: Sparkles, title: "Marketing y distribución", text: "Airbnb, Booking.com, Expedia, VRBO, canales directos y agencias partner." },
      { icon: User, title: "Experiencia del huésped", text: "Comunicación, check-in, conserjería y soporte 24/7 — hospitalidad consistente." },
      { icon: Wrench, title: "Mantenimiento y personal", text: "Cuidado preventivo, piscina y jardín, contratación, formación y nóminas." },
      { icon: ClipboardList, title: "Contabilidad y fiscalidad", text: "Estados mensuales, desglose de gastos, declaraciones locales y cumplimiento." },
      { icon: LineChart, title: "Informes transparentes", text: "Ocupación, ingresos vs. previsión, resultado neto y benchmarks cada mes." },
      { icon: Briefcase, title: "Un único contacto", text: "Un gestor de cuenta dedicado. Un informe. Sin logística entre zonas horarias." },
    ],
    mgmtPull: "Ingresos sin operaciones. Propiedad sin logística.",

    insideEyebrow: "Dentro de la guía",
    insideTitle: "Nueve capítulos escritos para quienes deciden.",
    chapters: [
      "Introducción — la oportunidad de Lombok",
      "Por qué Lombok es un mercado estratégico",
      "Comprar sobre plano (off-plan)",
      "Comprar una villa terminada",
      "Comprar terreno en Lombok",
      "Estructuras de propiedad para extranjeros",
      "Por qué importa el asesoramiento profesional",
      "Gestión integral — inversión verdaderamente pasiva",
      "Hablemos de tus objetivos de inversión",
    ],
  },
};

const InvestmentGuide = () => {
  const { lang } = useLang();
  const t = COPY[lang] || COPY.en;
  const [downloading, setDownloading] = useState(null);

  const handleDownload = async (guide) => {
    try {
      setDownloading(guide.lang);
      const res = await fetch(`${API}/guide/${guide.lang}`);
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = guide.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    } finally {
      setDownloading(null);
    }
  };

  const handlePreview = (guide) => {
    window.open(`${API}/guide/${guide.lang}`, "_blank", "noopener,noreferrer");
  };

  return (
    <section
      id="guide"
      data-testid="guide-section"
      className="bg-ivory text-charcoal"
    >
      {/* =============== HERO =============== */}
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10 pt-24 sm:pt-32 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <p
              className="text-[0.72rem] font-medium tracking-[0.22em] uppercase text-olive"
              data-testid="guide-kicker"
            >
              — {t.kicker}
            </p>
            <div className="mt-4 text-[0.68rem] tracking-[0.25em] uppercase text-volcanic/60 font-medium">
              {t.eyebrow}
            </div>
            <h2
              className="mt-6 font-heading font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
              data-testid="guide-title"
            >
              {t.heroTitleA}
              <em className="text-teakwood not-italic">{t.heroTitleLoc}</em>
              {t.heroTitleB}
            </h2>
            <p className="mt-8 max-w-xl text-base sm:text-lg text-volcanic font-light leading-[1.8]">
              {t.heroLede}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <a
                href="#guide-downloads"
                data-testid="guide-cta-scroll"
                className="group inline-flex items-center gap-3 bg-charcoal text-ivory px-8 py-4 text-[0.72rem] tracking-[0.22em] uppercase font-medium hover:bg-teakwood transition-colors duration-500"
              >
                {t.ctaDownload}
                <ArrowUpRight
                  size={16}
                  className="transition-transform duration-500 group-hover:translate-x-1"
                />
              </a>
              <span className="inline-flex items-center gap-2 text-[0.72rem] tracking-[0.22em] uppercase text-volcanic/70 font-medium">
                <FileText size={14} strokeWidth={1.6} />
                {t.heroMeta}
              </span>
            </div>
          </div>

          {/* Editorial book card */}
          <div className="lg:col-span-5 lg:pl-6">
            <div
              className="relative bg-charcoal text-ivory overflow-hidden shadow-[0_40px_60px_-40px_rgba(26,26,26,0.45)]"
              data-testid="guide-book-card"
            >
              {/* Book-like frame */}
              <div className="aspect-[3/4] p-8 sm:p-10 flex flex-col">
                <div className="flex items-center gap-3 text-[0.65rem] tracking-[0.3em] uppercase text-ivory/60 font-medium">
                  <span>Tenjara · Lombok</span>
                  <span className="flex-1 h-px bg-ivory/25" />
                </div>
                <div className="flex-1 flex flex-col justify-end pt-10">
                  <h3 className="font-heading text-5xl sm:text-6xl font-light leading-[0.95] tracking-tight text-ivory">
                    {t.cardTitle[0]}
                    <br />
                    <em className="text-gold not-italic font-normal">
                      {t.cardTitle[1]}
                    </em>
                  </h3>
                  <p className="mt-8 text-sm text-ivory/75 font-light leading-relaxed">
                    {t.cardSub}
                  </p>
                </div>
                <div className="mt-8 pt-6 border-t border-ivory/15 flex items-center justify-between text-[0.65rem] tracking-[0.25em] uppercase text-ivory/60 font-medium">
                  <span>2026 · Volume I</span>
                  <span>Tenjara Lombok</span>
                </div>
              </div>
              {/* Gold corner accent */}
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-gold/30 to-transparent pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {/* =============== STATS =============== */}
      <div className="bg-sand/60">
        <div className="mx-auto max-w-[1400px] px-6 sm:px-10 py-14">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6">
            {t.stats.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.label}
                  className="group"
                  data-testid={`guide-stat-${s.label.slice(0, 10)}`}
                >
                  <Icon
                    size={18}
                    strokeWidth={1.4}
                    className="text-teakwood"
                  />
                  <div className="mt-4 font-heading text-4xl sm:text-5xl font-light tracking-tight text-charcoal leading-none">
                    {s.value}
                  </div>
                  <div className="mt-3 h-px w-10 bg-charcoal/20 transition-all duration-500 group-hover:w-16 group-hover:bg-charcoal/50" />
                  <p className="mt-3 text-[0.85rem] leading-relaxed text-volcanic font-light">
                    {s.label}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* =============== DOWNLOAD CARDS =============== */}
      <div id="guide-downloads" className="mx-auto max-w-[1400px] px-6 sm:px-10 py-24 sm:py-28">
        <div className="max-w-3xl">
          <p
            className="text-[0.72rem] font-medium tracking-[0.22em] uppercase text-olive"
          >
            — {t.chooseEyebrow}
          </p>
          <h3 className="mt-6 font-heading font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight whitespace-pre-line">
            {t.chooseTitle}
          </h3>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10">
          {t.guides.map((g) => (
            <article
              key={g.lang}
              className="group relative bg-ivory border border-charcoal/15 p-8 sm:p-10 flex flex-col transition-all hover:border-charcoal"
              data-testid={`guide-card-${g.lang}`}
            >
              <div className="flex items-start justify-between">
                <div className="font-heading text-5xl sm:text-6xl text-teakwood font-light tracking-tight leading-none">
                  {g.code}
                </div>
                <div className="text-[0.65rem] tracking-[0.22em] uppercase text-volcanic/60 font-medium">
                  {g.meta}
                </div>
              </div>
              <h4 className="mt-8 font-heading font-normal text-3xl sm:text-4xl tracking-tight text-charcoal leading-tight">
                {g.title}
              </h4>
              <p className="mt-3 text-[0.95rem] text-volcanic font-light leading-relaxed">
                {g.subtitle}
              </p>
              <div className="mt-8 pt-6 border-t border-charcoal/10 flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => handleDownload(g)}
                  disabled={downloading === g.lang}
                  data-testid={`guide-download-${g.lang}`}
                  className="inline-flex w-full justify-center items-center gap-2 bg-charcoal text-ivory px-6 py-4 text-[0.72rem] tracking-[0.22em] uppercase font-medium hover:bg-teakwood transition-colors duration-500 disabled:opacity-60"
                >
                  {downloading === g.lang ? (
                    t.preparing
                  ) : (
                    <>
                      <Download size={14} strokeWidth={1.8} />
                      {t.downloadBtn}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => handlePreview(g)}
                  data-testid={`guide-preview-${g.lang}`}
                  className="inline-flex w-full justify-center items-center gap-2 border border-charcoal/20 px-6 py-4 text-[0.72rem] tracking-[0.22em] uppercase font-medium text-charcoal hover:border-charcoal hover:bg-charcoal hover:text-ivory transition-colors"
                >
                  <Eye size={14} strokeWidth={1.8} />
                  {t.previewBtn}
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* =============== FULL MANAGEMENT (dark band) =============== */}
      <div className="bg-charcoal text-ivory">
        <div className="mx-auto max-w-[1400px] px-6 sm:px-10 py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <p className="text-[0.72rem] font-medium tracking-[0.22em] uppercase text-gold">
                — {t.mgmtEyebrow}
              </p>
              <h2 className="mt-6 font-heading font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ivory whitespace-pre-line">
                {t.mgmtTitle}
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-base sm:text-lg text-ivory/75 font-light leading-[1.8]">
                {t.mgmtLede}
              </p>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
            {t.mgmtPoints.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="group"
                  data-testid={`mgmt-${p.title.slice(0, 10)}`}
                >
                  <div className="w-11 h-11 border border-gold/50 text-gold flex items-center justify-center transition-colors group-hover:bg-gold group-hover:text-charcoal">
                    <Icon size={18} strokeWidth={1.5} />
                  </div>
                  <h4 className="mt-6 font-heading text-2xl font-normal tracking-tight text-ivory leading-snug">
                    {p.title}
                  </h4>
                  <p className="mt-3 text-[0.9rem] text-ivory/70 font-light leading-relaxed">
                    {p.text}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-20 pt-10 border-t border-ivory/15">
            <p className="font-heading font-light text-3xl sm:text-4xl lg:text-5xl italic text-gold tracking-tight leading-[1.1]">
              “ {t.mgmtPull} ”
            </p>
          </div>
        </div>
      </div>

      {/* =============== CHAPTERS =============== */}
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10 py-24 sm:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
            <p className="text-[0.72rem] font-medium tracking-[0.22em] uppercase text-olive">
              — {t.insideEyebrow}
            </p>
            <h3 className="mt-6 font-heading font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight">
              {t.insideTitle}
            </h3>
          </div>
          <div className="lg:col-span-7">
            <ol className="divide-y divide-charcoal/10 border-t border-charcoal/10" data-testid="guide-chapters">
              {t.chapters.map((c, i) => (
                <li
                  key={c}
                  className="py-6 flex items-start gap-6 group"
                >
                  <span className="overline text-teakwood font-medium mt-1 shrink-0 w-10">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-heading text-xl sm:text-2xl font-light tracking-tight text-charcoal leading-snug transition-colors group-hover:text-teakwood">
                    {c}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InvestmentGuide;
